<template>
  <div>
    <!--    Champ de recherche par date-->
    <div class="mt-3 mb-5">
      <label
        for="date-search"
        class="mr-3"
      >{{ $t('appointmentPage.appointments.searchByDate') }}</label>
      <Calendar
        id="date-search"
        v-model="selectedDate"
        @date-select="searchByDate"
      />
    </div>

    <!--    Calendrier du bureau-->
    <FullCalendar
      ref="OfficeCalendar"
      :options="calendarOptions"
    >
      <template #eventContent="arg">
        <strong>{{ arg.timeText }}</strong>
        <Button
          v-if="arg.event.display !== 'background'"
          icon="pi pi-times"
          :title="$t('appointmentPage.appointments.removeAppointment')"
          class="p-button-rounded p-button-secondary"
          @click="removeAppointment(arg.event.id)"
        />
      </template>
    </FullCalendar>

    <!--    Fenêtre modale avec formulaire de création d'un créneau-->
    <Dialog
      id="appointment-dialog"
      v-model:visible="diplayAppointmentDialog"
      :breakpoints="{'1600px': '60vw', '960px': '75vw', '640px': '90vw'}"
    >
      <template #header>
        <h3>{{ $t('appointmentPage.appointments.editDialogHeader') }}</h3>
      </template>

      <div class="grid field">
        <label
          for="day"
          class="col-12 mb-2 md:col-5 md:mb-0"
        >{{ $t('entity.appointment.day') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="day"
            v-model="v$.appointment.day.$model"
            disabled
          />
        </div>
      </div>

      <div class="grid field">
        <label
          for="start"
          class="col-12 mb-2 md:col-5 md:mb-0 required-input"
        >{{ $t('entity.appointment.start') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="start"
            v-model="v$.appointment.start.$model"
            :time-only="true"
            hour-format="24"
            :class="{'invalid-input': v$.appointment.start.$error }"
          />
          <div
            v-for="error of v$.appointment.start.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <div class="grid field">
        <label
          for="end"
          class="col-12 mb-2 md:col-5 md:mb-0 required-input"
        >{{ $t('entity.appointment.end') }}</label>
        <div class="col-12 md:col-6">
          <Calendar
            id="end"
            v-model="v$.appointment.end.$model"
            :time-only="true"
            hour-format="24"
            :class="{'invalid-input': v$.appointment.end.$error }"
          />
          <div
            v-for="error of v$.appointment.end.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <Message
        v-if="appointmentErrors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of appointmentErrors"
            :key="error"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <template #footer>
        <Button
          :label="$t('action.back')"
          class="p-button-rounded p-button-outlined p-button-secondary"
          @click="closeAppointmentDialog"
        />
        <Button
          :disabled="v$.$invalid"
          :label="$t('action.confirm')"
          type="submit"
          class="p-button-rounded p-button-outlined"
          @click="validateAppointment(appointment)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction'
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/i18n/i18nValidators";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import {v4 as uuidv4} from 'uuid';
import {
  appointmentInBusinessHours,
  checkNoAppointmentOverlap,
  getAppointmentDuration,
  getTotalAppointmentsDuration
} from "@/shared/utils/appointmentSlotUtils";
import Message from "primevue/message";
import moment from "moment";
import {reactive} from "vue";
import useAppointmentFilter from "@/composables/filters/useAppointmentFilter";
import useAppointmentStatus from "@/composables/useAppointmentStatus";

export default {
  name: 'OfficeCalendar',
  components: { FullCalendar, Dialog, Button, Calendar, Message },
  props: {
    office: {
      type: Object,
      required: true
    },
    appointments: {
      type: Array,
      required: true
    },
    controlDuration: {
      type: Number,
      required: true
    }
  },
  emits: ['createAppointment', 'removeAppointment'],
  setup() {
    const appointmentFilter = reactive(useAppointmentFilter());
    const appointmentStatus = useAppointmentStatus()
    appointmentFilter.pageable.size = 1000;
    appointmentFilter.filters.status.value = [appointmentStatus.NO_SHOW, appointmentStatus.HONORED];
    return {
      v$: useVuelidate(),
      appointmentFilter
    }
  },
  data() {
    return {
      calendarOptions: {
        headerToolbar: {
          left: '',
          center: 'title',
          right: 'today prev,next'
        },
        plugins: [timeGridPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        allDaySlot: false,
        locales: [enLocale, frLocale],
        locale: this.$i18n.locale,
        slotMinTime: '08:00',
        slotMaxTime: '21:00',
        scrollTimeReset: false,
        businessHours: [],
        events: async ( fetchInfo, successCallback, failureCallback ) => {
          this.appointmentFilter.filters.start.value = new Date(fetchInfo.start);
          this.appointmentFilter.filters.end.value = new Date(fetchInfo.end);
          const events = await this.fetchUnavailable()
          successCallback(events)
        },
        eventOverlap: false,
        selectConstraint: 'businessHours',
        slotDuration: '00:20:00',
        selectable: true,
        selectMirror: true,
        selectOverlap: false,
        dateClick: this.dateClick,
        select: this.appointmentSelection,
      },
      diplayAppointmentDialog: false,
      appointmentErrors: [],
      unavailableAppointments: [],
      appointment: {

      },
      selectedDate: new Date(),
      errorAppointmentOverlap: 'validations.appointment.overlap',
      errorAppointmentExceedsControlDuration: 'validations.appointment.exceedsControlDuration',
      errorAppointmentGreaterThan0: 'validations.appointment.greaterThan0',
      windowWidth: window.innerWidth,
    }
  },
  validations() {
    return {
      appointment: {
        day: {},
        start: { required },
        end: {
          required,
        }
      }
    }
  },
  created() {
    window.addEventListener('resize', this.setCalendarView)
    this.$watch('$i18n.locale', (newVal) => this.calendarOptions.locale = newVal)
    this.$watch('appointments', appointments => {
      this.reloadUnavailable()
    })
    this.$emitter.on('book-failed', () => this.reloadUnavailable())
  },
  mounted() {
    this.initCalendarData()
  },
  unmounted() {
    window.removeEventListener('resize', this.setCalendarView)
  },
  methods: {
    initCalendarData() {
      this.getOfficePlanning()
      this.setCalendarView()
    },

    reloadUnavailable() {
      if (this.$refs.OfficeCalendar) {
        this.$refs.OfficeCalendar.getApi().refetchEvents()
      }
    },

    async fetchUnavailable(){
      const unavailable = []
      this.appointmentFilter.filters.officeId.value = this.office.id

      const {content} = await this.$appointmentService.getAll(
        this.appointmentFilter.getQueryParams())

      const appointmentsByDesk = content;
      if (appointmentsByDesk && appointmentsByDesk.length !== 0) {
        this.unavailableAppointments = appointmentsByDesk;

        appointmentsByDesk.forEach((appointment) => {
          unavailable.push({
            id: appointment.id,
            start: new Date(appointment.start),
            end: new Date(appointment.start.split('T')[0] + 'T' + appointment.end),
            allDay: false,
            display: 'background',
            overlap: 'false'
          })
        })
      }

      // Désactiver les jours avant la date d'ouverture
      let openDate = new Date(this.office.openDate)
      unavailable.push({
        start: new Date(openDate.setFullYear(openDate.getFullYear() - 5)),
        end: this.office.openDate,
        allDay: true,
        display: 'background',
        overlap: 'false'
      })
      // Désactiver les jours après la date de fermeture
      let closeDate = new Date(this.office.closeDate)
      unavailable.push({
        start: this.office.closeDate,
        end: new Date(closeDate.setFullYear(closeDate.getFullYear() + 5)),
        allDay: true,
        display: 'background',
        overlap: 'false'
      })

      unavailable.push(...this.appointments)
      return unavailable
    },

    remainingMinToSchedule() {
      let remainingMinutes = this.controlDuration - getTotalAppointmentsDuration(this.appointments);
      return remainingMinutes
    },

    async getOfficePlanning() {
      const planning = await this.$planningService.fetchByOffice(this.office.id)
      const dayPlannings = [
        planning.sundayPlanning,
        planning.mondayPlanning,
        planning.tuesdayPlanning,
        planning.wednesdayPlanning,
        planning.thursdayPlanning,
        planning.fridayPlanning,
        planning.saturdayPlanning,
      ]
      let businessHours = []
      let i = 0
      for (const dayPlanning of dayPlannings) {
        if (dayPlanning !== null) {
          if (dayPlanning.morningOpen !== null && dayPlanning.morningClose !== null) {
            businessHours.push({
              daysOfWeek: [i],
              startTime: dayPlanning.morningOpen.slice(0, -2),
              endTime: dayPlanning.morningClose.slice(0, -2)
            })
          }
          if (dayPlanning.afternoonOpen !== null && dayPlanning.afternoonClose !== null) {
            businessHours.push({
              daysOfWeek: [i],
              startTime: dayPlanning.afternoonOpen.slice(0, -2),
              endTime: dayPlanning.afternoonClose.slice(0, -2)
            })
          }
        } else {
          businessHours.push({
            daysOfWeek: [i],
            startTime: "00:00",
            endTime: "00:00"
          })
        }
        i++
      }

      this.calendarOptions.businessHours = businessHours
    },

    searchByDate(value) {
      let fullCalendarApi = this.$refs.OfficeCalendar.getApi();
      fullCalendarApi.gotoDate(value)
    },

    dateClick(arg) {
      let isFutureDate = moment(new Date(arg.dateStr)).isAfter(new Date(), 'day')
      if (isFutureDate) {
        let appointment = {
          start: new Date(arg.dateStr),
        }
        this.openAppointmentDialog(appointment)
      }
    },
    appointmentSelection(arg) {
      let appointment = {
        start: arg.start,
        end: arg.end
      }
      this.openAppointmentDialog(appointment)
    },

    validateAppointment(appointment) {
      this.appointmentErrors = []

      let newAppointment = JSON.parse(JSON.stringify(appointment))
      newAppointment.day = new Date(newAppointment.day)
      newAppointment.start = new Date(newAppointment.start)
      newAppointment.end = new Date(newAppointment.end)
      // Formattage de la date de début et fin du créneau à créer
      let appointmentStart = new Date(newAppointment.day.toISOString().split('T')[0] + 'T' + newAppointment.start.toISOString().split('T')[1]);
      appointmentStart.setSeconds(0, 0)
      let appointmentEnd = new Date(newAppointment.day.toISOString().split('T')[0] + 'T' + newAppointment.end.toISOString().split('T')[1]);
      appointmentEnd.setSeconds(0, 0)

      let appointmentToCreate = {
        day: newAppointment.day,
        start: appointmentStart.toISOString(),
        end: appointmentEnd.toISOString(),
        office: this.office
      }

      let appointmentDurationMins = getAppointmentDuration(appointmentToCreate)
      // Vérifier que la durée du créneau sélectionné est supérieure à 0
      if (appointmentDurationMins <= 0) {
        this.appointmentErrors.push(this.errorAppointmentGreaterThan0)
      }

      // Vérifier que le créneau n'excède pas la durée estimée du créneau
      if (appointmentDurationMins > this.controlDuration) {
        this.appointmentErrors.push(this.errorAppointmentExceedsControlDuration)
      }

      // Vérifier qu'il n'y a pas de chevauchement avec les autres créneaux
      if (!checkNoAppointmentOverlap(appointmentToCreate, this.unavailableAppointments) ||
        !appointmentInBusinessHours(appointmentToCreate, this.calendarOptions.businessHours)) {
        this.appointmentErrors.push(this.errorAppointmentOverlap)
      }

      if (this.appointmentErrors.length === 0) {
        this.addAppointment(appointmentToCreate)
      }
    },

    addAppointment(appointment) {
      this.appointmentErrors = []
      appointment.id = uuidv4()
      this.$emit('createAppointment', appointment)
      this.appointment = {}
      this.diplayAppointmentDialog = false

      this.$refs.OfficeCalendar.getApi().refetchEvents()
    },

    removeAppointment(appointmentId) {
      const appointment = this.appointments.find(s => s.id === appointmentId)
      this.$emit('removeAppointment', appointment)
      this.$refs.OfficeCalendar.getApi().refetchEvents()
    },

    openAppointmentDialog(appointment) {
      this.appointmentErrors = []
      this.diplayAppointmentDialog = true

      this.appointment = { ...appointment }
      this.appointment.day = appointment.start
      const avaibleSheduleTime = this.remainingMinToSchedule()
      this.appointment.end = moment(appointment.start).add(avaibleSheduleTime, 'minutes').toDate()
    },
    closeAppointmentDialog() {
      this.diplayAppointmentDialog = false
    },
    setCalendarView() {
      // En vue mobile, le calendrier n'affiche qu'une seule journée.
      const mobileBreakpoint = 576;
      this.windowWidth = window.innerWidth;
      let fullCalendarApi = this.$refs.OfficeCalendar.getApi();
      if (this.windowWidth < mobileBreakpoint) {
        fullCalendarApi.changeView('timeGridDay');
      } else {
        fullCalendarApi.changeView('timeGridWeek');
      }
    }
  }
}
</script>

<style lang="scss" scoped>

h3 {
  margin: 0;
}

</style>
